'use client';

import React, { useState } from 'react';
import { AccordionComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface AccordionProps {
  component: AccordionComponent;
}

export const Accordion: React.FC<AccordionProps> = ({ component }) => {
  const { props } = component;
  const [openSections, setOpenSections] = useState<string[]>(
    props.sections.filter(s => s.defaultOpen).map(s => s.id)
  );

  const toggleSection = (sectionId: string) => {
    if (props.allowMultiple) {
      setOpenSections(prev =>
        prev.includes(sectionId)
          ? prev.filter(id => id !== sectionId)
          : [...prev, sectionId]
      );
    } else {
      setOpenSections(prev =>
        prev.includes(sectionId) ? [] : [sectionId]
      );
    }
  };

  const getVariantColors = () => {
    switch (props.variant) {
      case 'primary':
        return { header: 'bg-learn-sage-100 hover:bg-learn-sage-200', border: 'border-learn-sage-200', text: 'text-learn-sage-900' };
      case 'success':
        return { header: 'bg-green-100 hover:bg-green-200', border: 'border-green-200', text: 'text-green-900' };
      case 'info':
        return { header: 'bg-learn-sky-100 hover:bg-learn-sky-200', border: 'border-learn-sky-200', text: 'text-learn-sky-900' };
      case 'highlight':
        return { header: 'bg-learn-coral-100 hover:bg-learn-coral-200', border: 'border-learn-coral-200', text: 'text-learn-coral-900' };
      default:
        return { header: 'bg-gray-50 hover:bg-gray-100', border: 'border-gray-200', text: 'text-gray-900' };
    }
  };

  const colors = getVariantColors();

  return (
    <div className="w-full space-y-3">
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}

      {props.sections.map((section, index) => {
        const isOpen = openSections.includes(section.id);
        
        return (
          <div
            key={section.id}
            className={`border ${colors.border} rounded-lg overflow-hidden transition-all duration-200`}
          >
            {/* Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className={`w-full ${colors.header} px-4 py-3 flex items-center justify-between transition-colors`}
            >
              <div className="flex items-center gap-3 flex-1 text-left">
                {section.icon && (
                  <ComponentIcon icon={section.icon} className={`w-5 h-5 ${colors.text}`} />
                )}
                <span className={`text-base font-semibold ${colors.text}`}>
                  {section.title}
                </span>
              </div>
              <div className={colors.text}>
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </button>

            {/* Content */}
            <div
              className={`transition-all duration-300 ease-in-out ${
                isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}
            >
              <div className="p-4 bg-white">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {section.content}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

